import * as Sentry from "@sentry/react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import { LinkContainer } from "react-router-bootstrap";
import { Link, Outlet } from "react-router-dom";

import { getApiHostname } from "@inrange/building-manager-api-client/config";
import { KINDE_ENVIRONMENT } from "@inrange/shared-components";
import { InRangeLogoWhite } from "@inrange/theme-components/icons";
import { useContext } from "react";
import { UserContext } from "../auth/UserContext";

function Template() {
  const { user, logout } = useContext(UserContext);
  const doLogout = () => {
    if (logout) {
      console.log("User logout");
      if (import.meta.env.PROD) {
        Sentry.setUser(null);
      }
      logout();
    }
  };

  const apiHostname = getApiHostname();
  const prodApiHostname = "https://bma-api.inrange.io";
  const isUsingProdApiHostname = apiHostname === prodApiHostname;
  const isUsingProdKindeEnv = import.meta.env.VITE_KINDE_AUTH_ENV === "PROD";

  const domain = window.location.hostname;
  const prodDomains = new Set([
    // User facing domain
    "admin.inrange.io",
  ]);
  const isProdDomain = prodDomains.has(domain);

  // Flex column with `height: 100vh` so pages can opt into filling
  // the remaining viewport height with `flex: 1; min-height: 0`
  // (see /leads, /leads/search) instead of having to know the exact
  // pixel height of the (variable) non-prod banner + main nav.
  // Pages that rely on natural document flow still render identically
  // because we don't set `overflow: hidden` here - their tall content
  // overflows the wrapper visually and the body still scrolls. The
  // difference vs. `min-height: 100vh` is that with a hard `height`
  // the wrapper won't *grow* past the viewport, so a child's
  // `flex: 1; min-height: 0` is now properly bounded - previously the
  // chain could inflate when the right panel of /leads/search was
  // taller than the viewport, taking the left panel with it.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      {!isProdDomain && (
        <Navbar
          bg={
            isUsingProdApiHostname || isUsingProdKindeEnv ? "warning" : "info"
          }
          expand="md"
        >
          <Container>
            <Navbar.Brand as={Link} to="/">
              {isUsingProdApiHostname || isUsingProdKindeEnv
                ? "WARNING: Non-Prod website connecting to Prod API"
                : "INFO: Non-Prod website connecting to Non-Prod API"}
            </Navbar.Brand>
          </Container>
        </Navbar>
      )}
      <Navbar
        expand="md"
        style={{
          backgroundColor: "#20124d",
          padding: "8px 20px",
          // Establish a stacking context above page content so the
          // user-menu dropdown (Manage Access / Logout) paints over the
          // SiteNav tab bar, which sets zIndex: 1000 and renders later
          // in the DOM (inside <Outlet/>). Without this the header
          // dropdown and SiteNav tie at z-index 1000 and SiteNav wins.
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Navbar.Brand as={Link} to="/">
          <img src={InRangeLogoWhite} alt="InRange Logo" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav">
          <span className="navicon"></span>
        </Navbar.Toggle>
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <LinkContainer to="/operational">
              <Nav.Link>Operational</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/organisation">
              <Nav.Link>Organisations</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/site">
              <Nav.Link>Sites</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/site/new">
              <Nav.Link>New Site</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/leads">
              <Nav.Link>Leads</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/data-config">
              <Nav.Link>Data & Config</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/costs">
              <Nav.Link>Costs</Nav.Link>
            </LinkContainer>
            <LinkContainer to="/demand">
              <Nav.Link>Demand Data</Nav.Link>
            </LinkContainer>
          </Nav>
          <Navbar>
            <NavDropdown title={user!.name} id="basic-nav-dropdown">
              <NavDropdown.Item
                target="_blank"
                href={`https://${KINDE_ENVIRONMENT.KINDE_MANAGEMENT_DOMAIN}/admin/cx/_:nav&m:users::_:submenu&s:list`}
              >
                Manage Access
              </NavDropdown.Item>
              <NavDropdown.Item onClick={doLogout}>Logout</NavDropdown.Item>
            </NavDropdown>
          </Navbar>
        </Navbar.Collapse>
      </Navbar>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          // Pages that don't opt into the flex chain (the majority -
          // natural document flow with their own internal Container)
          // need somewhere to scroll now that the outer wrapper is
          // capped at `100vh` instead of `min-height: 100vh`. This
          // wrapper takes over from `body` as the page-level scroll
          // container. Pages that DO use `flex: 1; min-height: 0`
          // (e.g. /leads, /leads/search) don't trigger this scroll
          // because they constrain their own children.
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Outlet />
      </div>
    </div>
  );
}

export default Template;
